<template>
  <home-hero-skeleton v-if="loading" />
  <section v-else class="home-hero" :class="{ 'home-hero--single': carouselSlides.length === 1 }" aria-label="Destaques">
    <div class="home-hero__viewport">
      <vue-slick-carousel
        ref="heroSlick"
        :key="carouselKey"
        class="home-hero__slick"
        :dots="false"
        :arrows="false"
        :infinite="carouselSlides.length > 1"
        :autoplay="carouselSlides.length > 1"
        :autoplay-speed="6000"
        :speed="650"
        css-ease="cubic-bezier(0.25, 0.1, 0.25, 1)"
        :slides-to-show="1"
        :slides-to-scroll="1"
        :center-mode="carouselSlides.length > 1"
        center-padding="18%"
        :adaptive-height="false"
        :rows="1"
        :responsive="responsiveSettings"
        @afterChange="onAfterChange"
      >
        <article
          v-for="(slide, index) in carouselSlides"
          :key="slide.id || `${index}-${slide.name}`"
          class="home-hero__slide"
          :aria-hidden="index !== activeIndex"
        >
          <img
            class="home-hero__image"
            :src="slide.image"
            :alt="index === activeIndex ? slide.name : ''"
            :loading="slide.priority"
            @error="onImageError"
          />
          <div class="home-hero__content">
            <h1 class="home-hero__title">{{ slide.name }}</h1>
            <p v-if="slide.description" class="home-hero__description">{{ slide.description }}</p>
            <a
              v-if="safeHttpUrl(slide.href)"
              class="home-hero__cta"
              :href="safeHttpUrl(slide.href)"
              :target="slide.isExternal ? '_blank' : null"
              :rel="slide.isExternal ? 'noopener noreferrer' : null"
            >
              {{ slide.ctaLabel }}
              <v-icon>mdi-arrow-right</v-icon>
            </a>
          </div>
        </article>
      </vue-slick-carousel>
    </div>
    <div v-if="carouselSlides.length > 1" class="home-hero__controls">
      <button type="button" class="home-hero__arrow" aria-label="Destaque anterior" @click="prev">
        <v-icon>mdi-chevron-left</v-icon>
      </button>
      <div class="home-hero__dots" aria-label="Indicadores do hero">
        <button
          v-for="(slide, index) in carouselSlides"
          :key="slide.id || index"
          type="button"
          class="home-hero__dot"
          :class="{ 'home-hero__dot--active': index === activeIndex }"
          :aria-label="`Mostrar destaque ${index + 1}`"
          @click="goTo(index)"
        ></button>
      </div>
      <button type="button" class="home-hero__arrow" aria-label="Proximo destaque" @click="next">
        <v-icon>mdi-chevron-right</v-icon>
      </button>
    </div>
  </section>
</template>

<script>
import HomeHeroSkeleton from "@/components/home/states/HomeHeroSkeleton.vue";
import { HOME_BANNER_FALLBACK, setImageFallback } from "@/utils/homeImageFallbacks";
import VueSlickCarousel from "vue-slick-carousel";
import "vue-slick-carousel/dist/vue-slick-carousel.css";
import "vue-slick-carousel/dist/vue-slick-carousel-theme.css";
import { sanitizeHttpUrl } from "@/utils/safeNavigation";

export default {
  name: "HomeHeroSection",
  components: { HomeHeroSkeleton, VueSlickCarousel },
  props: {
    slides: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      activeIndex: 0,
    };
  },
  computed: {
    fallbackSlide() {
      return {
        name: "Zinnes",
        eyebrow: "Destaque Zinnes",
        description: "A sua plataforma de quadrinhos.",
        image: HOME_BANNER_FALLBACK,
        href: "/descobrir/categorias",
        ctaLabel: "Explorar obras",
      };
    },
    carouselSlides() {
      return this.slides.length ? this.slides : [this.fallbackSlide];
    },
    carouselKey() {
      return this.carouselSlides.map(slide => slide.id || slide.image || slide.name).join("|");
    },
    responsiveSettings() {
      return [
        {
          breakpoint: 720,
          settings: {
            centerMode: false,
            centerPadding: "0px",
            slidesToShow: 1,
          },
        },
      ];
    },
  },
  mounted() {
    this.refreshCarousel();
  },
  watch: {
    slides() {
      this.activeIndex = 0;
      this.refreshCarousel();
    },
    loading(isLoading) {
      if (!isLoading) this.refreshCarousel();
    },
  },
  methods: {
    safeHttpUrl: sanitizeHttpUrl,
    refreshCarousel() {
      this.$nextTick(() => {
        const slick = this.$refs.heroSlick;
        if (slick && typeof slick.goTo === "function") slick.goTo(0, true);
      });
    },
    prev() {
      const slick = this.$refs.heroSlick;
      if (slick && typeof slick.prev === "function") {
        slick.prev();
      }
    },
    next() {
      const slick = this.$refs.heroSlick;
      if (slick && typeof slick.next === "function") {
        slick.next();
      }
    },
    goTo(index) {
      const slick = this.$refs.heroSlick;
      if (slick && typeof slick.goTo === "function") {
        slick.goTo(index);
      } else {
        this.activeIndex = index;
      }
    },
    onAfterChange(index) {
      this.activeIndex = typeof index === "number" ? index : 0;
    },
    onImageError(event) {
      setImageFallback(event, HOME_BANNER_FALLBACK);
    },
  },
};
</script>
